import { dehydrate, QueryClient } from '@tanstack/react-query';
import { STATUS_CODE } from '@kc-console/core';

import { printGuardLog } from '../../utils/printGuardLog';
import { isServerTraceEnabled } from '../../utils/serverTrace/sampling';
import {
  addServerTraceGuardsDuration,
  appendServerTraceResponseHeaders,
  beginServerTraceExecution,
} from '../../utils/serverTrace/store';
import { updateGuardsCookie } from '../../utils/updateGuardsCookie';
import {
  updateServiceAppInitialState,
  updateServiceAppRouteState,
} from '../../utils/updateGuardsState';

import type { GetServerSidePropsContext, Redirect } from 'next';
import type { BaseState, PageComponentProps, ServiceAppState } from '@kc-console/context';
import type { Guard } from '../../types';
import type { ServerTraceExecutionResult } from '../../utils/serverTrace/types';

type ExecuteServerSideGuardsParams<T extends BaseState | ServiceAppState> = {
  context: GetServerSidePropsContext;
  guards: Guard<T>;
  state: T;
  shouldUpdateAppInitialState?: boolean;
};

/**
 * 초기 진입 시, 서버사이드에서 등록된 가드를 실행하는 함수
 * @description
 * - 초기 진입 시에만 실행
 * - serviceInitialGuards 는 SSR 지원이 hosts 레벨에서 꼭 필요한 경우, 기본 등록된 가드 리스트 가장 마지막에 추가 가드를 붙일 때 사용
 * @param context
 * @param serviceInitialGuards
 */
export const executeServerSideGuards = async <T extends BaseState | ServiceAppState>({
  context,
  guards,
  state: initialState,
  shouldUpdateAppInitialState = false,
}: ExecuteServerSideGuardsParams<T>): Promise<
  { props: PageComponentProps } | { redirect: Redirect }
> => {
  const queryClient = new QueryClient();
  const execution = beginServerTraceExecution(
    context,
    shouldUpdateAppInitialState ? 'initial' : 'route',
  );
  let executionResult: ServerTraceExecutionResult = {
    outcome: 'rejected',
    service: initialState.serviceKeyword,
  };
  const traceEnabled = isServerTraceEnabled();

  try {
    const guardsStartedAt = traceEnabled
      ? (globalThis.performance?.now() ?? Date.now())
      : undefined;
    let state: Partial<T>;

    try {
      state = await guards({
        context,
        state: initialState,
        queryClient,
      });
    } finally {
      if (guardsStartedAt !== undefined) {
        const guardsSettledAt = globalThis.performance?.now() ?? Date.now();
        addServerTraceGuardsDuration(context.req, guardsSettledAt - guardsStartedAt);
      }
    }

    if (shouldUpdateAppInitialState) {
      // 쿠키에 결과 저장
      updateGuardsCookie({ context, state });
      // 쿼리키에 결과 저장
      updateServiceAppInitialState({ queryClient, state });
      // 가드 완료 로그 저장
      printGuardLog({
        context,
        state: state,
        guardName: 'initialGuards',
        forced: true,
      });
    }

    updateServiceAppRouteState({ queryClient, state });

    executionResult = {
      outcome: state.redirect
        ? 'redirect'
        : state.status !== STATUS_CODE.OK
          ? 'failed_status'
          : 'success',
      statusCode: state.status,
      service: state.serviceKeyword,
    };

    if (state.redirect) {
      return { redirect: state.redirect };
    }

    return {
      props: {
        ...state.ssrConfig,
        dehydratedState: dehydrate(queryClient),
        state: { status: state.status || STATUS_CODE.INTERNAL_SERVER_ERROR },
      } as PageComponentProps,
    };
  } finally {
    execution.end(executionResult);
    appendServerTraceResponseHeaders(context);
  }
};

/**
 * cookie나 state 연동 없는 executor - 주로 route guard 실행 시 사용한다.
 * @param context
 * @param guards
 * @param initialState
 */
export const executeStatelessServerSideGuards = async ({
  context,
  guards,
  state: initialState,
}: ExecuteServerSideGuardsParams<BaseState>): Promise<
  { props: PageComponentProps } | { redirect: Redirect }
> => {
  return await executeServerSideGuards<BaseState>({
    context,
    guards,
    state: initialState,
    shouldUpdateAppInitialState: false,
  });
};

/**
 * cookie나 state 연동 하는 executor - 주로 initial guard 실행 시 사용한다.
 * @param context
 * @param guards
 * @param initialState
 */
export const executeStatefulServerSideGuards = async ({
  context,
  guards,
  state: initialState,
}: ExecuteServerSideGuardsParams<ServiceAppState>): Promise<
  { props: PageComponentProps } | { redirect: Redirect }
> => {
  return await executeServerSideGuards<ServiceAppState>({
    context,
    guards,
    state: initialState,
    shouldUpdateAppInitialState: true,
  });
};
